import { createSlice } from '@reduxjs/toolkit';
import { Nullable } from '@jamesgmarks/utilities';
import { TCurrencyCode } from '@llws/hydra-shared';
import { TLoadedState, TSaveState } from 'src/types/TLoadedState';

/**
 * Shape of a single invoice preview split stored in Redux.
 * Mirrors the backend `IInvoicePreviewData` shape; `title` is populated by the server
 * after matching the split against historical invoices so the UI can display a
 * meaningful name (e.g. `220728-A`) instead of a raw invoice number.
 */
interface IInvoicePreviewData {
  /** Display title derived from historical invoice matching (e.g. "220728-A"). */
  title?: string;
  invoiceNumber: string;
  originalInvoiceNumber?: string;
  address: {
    organization?: string;
    street?: string;
    street2?: string;
    city?: string;
    province?: string;
    country?: string;
    postalCode?: string;
  };
  items: {
    item: string;
    description: string;
    unitCost: number;
    quantity: number;
    lineTotal: number;
  }[];
  taxTotal: number;
  total: number;
  currency: TCurrencyCode
};

export interface IInvoicePreviewState {
  previewData: Nullable<IInvoicePreviewData[]>;
  skippedMonths: string[];
  loadedState: TLoadedState;
  invoiceGroupSaveState: TSaveState
}

const initialState: IInvoicePreviewState = {
  previewData: null,
  skippedMonths: [],
  loadedState: 'idle',
  invoiceGroupSaveState: 'idle',
};

export const invoiceManagementSlice = createSlice({
  name: 'invoiceManagementSlice',
  initialState,
  reducers: {
    invoicePreviewReceived: (state, action) => {
      state.previewData = action.payload.data;
      state.skippedMonths = action.payload.skippedMonths ?? [];
      state.loadedState = 'loaded';
    },
    setLoadedState: (state, action) => {
      state.loadedState = action.payload;
    },
    setInvoiceGroupSaveState: (state, action) => {
      state.invoiceGroupSaveState = action.payload;
    },
    clearPreviewData: (state) => {
      state.previewData = null;
      state.skippedMonths = [];
      state.loadedState = 'idle';
    },
  },
});

export const {
  invoicePreviewReceived,
  setLoadedState,
  setInvoiceGroupSaveState,
  clearPreviewData,
} = invoiceManagementSlice.actions;

export default invoiceManagementSlice.reducer;
